import React, { useState, useEffect } from 'react';
import api from '../../utils/axiosConfig';
import { formatDateDDMMYYYY } from '../../utils/dateFormat';
import { useNavigate } from 'react-router-dom';
import PageLoadingArea from '../../components/PageLoader/PageLoadingArea';
import usePageLoading from '../../hooks/usePageLoading';
import '../../styles/ListPage.css';

function QuotationsList() {
    const navigate = useNavigate();
    const { isLoading, withLoading } = usePageLoading(true);
    const [quotations, setQuotations] = useState([]);
    const [selectedQuotations, setSelectedQuotations] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('All');
    const [filteredQuotations, setFilteredQuotations] = useState([]);

    useEffect(() => {
        withLoading(async () => {
            const res = await api.get('/quotations');
            const sortedQuo = res.data.sort((a, b) => new Date(b.date) - new Date(a.date));
            setQuotations(sortedQuo);
            setFilteredQuotations(sortedQuo);
        }).catch((err) => console.error('Error fetching quotations:', err));
    }, [withLoading]);

    useEffect(() => {
        const results = quotations.filter(quotation => {
            const matchesTitle = quotation.subject.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesQuotationTo = quotation.quotationTo.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesQuotationNo = quotation.quotationNo.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesStatus = statusFilter === 'All' || quotation.status === statusFilter;
            return (matchesTitle || matchesQuotationTo || matchesQuotationNo) && matchesStatus;
        });
        setFilteredQuotations(results);
    }, [searchTerm, statusFilter, quotations]);

    const handleCheckboxChange = (quotationId) => {
        setSelectedQuotations(prev =>
            prev.includes(quotationId) ? prev.filter(id => id !== quotationId) : [...prev, quotationId]
        );
    };

    const handleSelectAll = (e) => {
        setSelectedQuotations(e.target.checked ? filteredQuotations.map(q => q.id) : []);
    };

    const handleDeleteSelected = async () => {
        if (!window.confirm('Are you sure you want to delete the selected quotations?')) return;
        try {
            await Promise.all(selectedQuotations.map(id => api.delete(`/quotations/${id}`)));
            setQuotations(prev => prev.filter(q => !selectedQuotations.includes(q.id)));
            setSelectedQuotations([]);
        } catch (error) {
            console.error('Error deleting quotations:', error);
            alert('Failed to delete selected quotations');
        }
    };

    const statusClass = (status) => {
        if (status === 'Pending') return 'list-status pending';
        if (status === 'Approved') return 'list-status approved';
        if (status === 'Rejected') return 'list-status rejected';
        return 'list-status';
    };

    const allSelected = filteredQuotations.length > 0 && selectedQuotations.length === filteredQuotations.length;

    return (
        <PageLoadingArea isLoading={isLoading}>
        <div className="list-page">
            <div className="list-page-header">
                <div className="list-page-title">
                    <h1>Quotations</h1>
                    <p className="list-page-subtitle">
                        {filteredQuotations.length} quotation{filteredQuotations.length === 1 ? '' : 's'}
                    </p>
                </div>
                <div className="list-header-actions">
                    <div className="list-search-wrap">
                        <input
                            type="text"
                            placeholder="Search quotation, party, or subject"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="list-search-input"
                        />
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="list-filter-select"
                        >
                            <option value="All">All</option>
                            <option value="Pending">Pending</option>
                            <option value="Approved">Approved</option>
                            <option value="Rejected">Rejected</option>
                        </select>
                    </div>
                    <div className="list-button-group">
                        <button type="button" onClick={() => navigate('/createQuotations')} className="list-add-button">
                            Add New Quotation
                        </button>
                        {selectedQuotations.length > 0 && (
                            <button type="button" onClick={handleDeleteSelected} className="list-delete-button">
                                Delete ({selectedQuotations.length})
                            </button>
                        )}
                    </div>
                </div>
            </div>

            <div className="list-table-card">
                <div className="list-table-scroll">
                    <table className="list-table">
                        <thead>
                            <tr>
                                <th className="col-check">
                                    <input type="checkbox" checked={allSelected} onChange={handleSelectAll} aria-label="Select all" />
                                </th>
                                <th>Sr No</th>
                                <th>Date</th>
                                <th>Quotation No</th>
                                <th>Quotation To</th>
                                <th>Subject</th>
                                <th>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredQuotations.length === 0 ? (
                                <tr className="list-empty-row">
                                    <td colSpan="7">
                                        <div className="list-empty-state">
                                            <h3>No quotations found</h3>
                                            <p>{searchTerm || statusFilter !== 'All' ? 'Try different filters.' : 'Create your first quotation to get started.'}</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : filteredQuotations.map((quotation, index) => (
                                <tr
                                    key={quotation.id}
                                    className={`list-row${selectedQuotations.includes(quotation.id) ? ' is-selected' : ''}`}
                                    onClick={(e) => {
                                        if (e.target.type !== 'checkbox') navigate(`/createQuotations/${quotation.id}`);
                                    }}
                                >
                                    <td className="col-check" onClick={(e) => e.stopPropagation()}>
                                        <input
                                            type="checkbox"
                                            checked={selectedQuotations.includes(quotation.id)}
                                            onChange={() => handleCheckboxChange(quotation.id)}
                                        />
                                    </td>
                                    <td>{index + 1}</td>
                                    <td>{formatDateDDMMYYYY(quotation.date)}</td>
                                    <td><span className="list-primary-text">{quotation.quotationNo}</span></td>
                                    <td>{quotation.quotationTo}</td>
                                    <td><span className="list-muted-text">{quotation.subject}</span></td>
                                    <td><span className={statusClass(quotation.status)}>{quotation.status}</span></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
        </PageLoadingArea>
    );
}

export default QuotationsList;
